import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HttpService } from '../../services/httpService.service';
import { PaymentContext } from '../../interfaces/payment-gateway.interface';

interface StripePaymentContext {
  customerId: string;
  subscriptionId?: string;
  planId?: string;
  planName?: string;
  amount?: number;
  context: PaymentContext;
  isAutoRenew?: boolean;
  isConvertToPrepaid?: boolean;
  taxAmount?: number;
  taxIncluded?: boolean;
  billingAddress?: any;
  customerZipCodeId?: string | null;
  planPrice?: number;
  planChangePaymentType?: string | null;
  cartItems?: any[];
  returnUrl?: string;
  metadata?: Record<string, any>;
}

type PageState = 'processing' | 'success' | 'error' | 'cancelled' | 'no-context';

@Component({
  selector: 'app-stripe-success',
  template: `
    <div class="d-flex align-items-center justify-content-center" style="min-height: 60vh; padding: 1rem;">
      <div class="card shadow text-center" style="max-width: 420px; width: 100%; padding: 2rem; border-radius: 12px;">

        <!-- Processing -->
        <ng-container *ngIf="state === 'processing'">
          <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status"></div>
          <h5 class="mt-2">Processing your payment...</h5>
          <p class="text-muted small">Please do not close this page.</p>
        </ng-container>

        <!-- Success -->
        <ng-container *ngIf="state === 'success'">
          <div class="rounded-circle bg-success d-inline-flex align-items-center justify-content-center mb-3"
               style="width: 64px; height: 64px;">
            <i class="fa fa-check text-white" style="font-size: 1.8rem;"></i>
          </div>
          <h5 class="text-success">Payment Successful</h5>
          <p class="text-muted small mb-3">{{ successMessage }}</p>
          <p class="text-muted" style="font-size: 0.75rem;">Redirecting in {{ countdown }}s...</p>
        </ng-container>

        <!-- Error -->
        <ng-container *ngIf="state === 'error'">
          <div class="rounded-circle bg-danger d-inline-flex align-items-center justify-content-center mb-3"
               style="width: 64px; height: 64px;">
            <i class="fa fa-times text-white" style="font-size: 1.8rem;"></i>
          </div>
          <h5 class="text-danger">Payment Failed</h5>
          <p class="text-danger small mb-3">{{ errorMessage }}</p>
          <button class="btn btn-dark btn-sm" (click)="goBack()">Go Back</button>
        </ng-container>

        <!-- Cancelled -->
        <ng-container *ngIf="state === 'cancelled'">
          <div class="rounded-circle bg-warning d-inline-flex align-items-center justify-content-center mb-3"
               style="width: 64px; height: 64px;">
            <i class="fa fa-exclamation text-white" style="font-size: 1.8rem;"></i>
          </div>
          <h5>Payment Cancelled</h5>
          <p class="text-muted small mb-3">Your payment was not completed.</p>
          <button class="btn btn-dark btn-sm" (click)="goBack()">Go Back</button>
        </ng-container>

        <!-- No Context -->
        <ng-container *ngIf="state === 'no-context'">
          <div class="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center mb-3"
               style="width: 64px; height: 64px;">
            <i class="fa fa-info text-white" style="font-size: 1.8rem;"></i>
          </div>
          <h5>Invalid Payment Session</h5>
          <p class="text-muted small mb-3">
            No payment session found. This may happen if you refreshed the page or accessed it directly.
          </p>
          <button class="btn btn-dark btn-sm" (click)="goBack()">Go Back</button>
        </ng-container>

      </div>
    </div>
  `
})
export class StripeSuccessComponent implements OnInit, OnDestroy {
  state: PageState  = 'processing';
  successMessage    = '';
  errorMessage      = '';
  countdown         = 3;

  private countdownTimer: any = null;
  private returnUrl           = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private httpService: HttpService
  ) {}

  ngOnInit(): void {
    this.handleReturn();
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) { clearInterval(this.countdownTimer); }
  }

  private async handleReturn(): Promise<void> {
    const sessionId: string = this.route.snapshot.queryParams['session_id'] || '';

    if (this.router.url.includes('/cancel')) {
      this.state = 'cancelled';
      return;
    }

    if (!sessionId) {
      this.state = 'cancelled';
      return;
    }

    // Read and validate stored context
    let ctx: StripePaymentContext | null = null;
    try {
      const stored = localStorage.getItem('stripePaymentContext');
      if (!stored) { this.state = 'no-context'; return; }
      ctx = JSON.parse(stored) as StripePaymentContext;
    } catch {
      this.state = 'no-context';
      return;
    }

    if (!ctx || !ctx.customerId) {
      this.state = 'no-context';
      return;
    }

    // Capture return URL before any async work
    this.returnUrl = ctx.returnUrl || '';

    try {
      await this.processPayment(sessionId, ctx);
      this.state          = 'success';
      this.successMessage = this.getSuccessMessage(ctx.context);
      this.cleanup();
      this.startCountdown();
    } catch (err: any) {
      console.error('[StripeSuccess] Processing failed:', err);
      this.state        = 'error';
      this.errorMessage = err?.message || 'An unexpected error occurred.';
      this.cleanup();
    }
  }

  private async processPayment(sessionId: string, ctx: StripePaymentContext): Promise<void> {
    switch (ctx.context) {
      case PaymentContext.TOP_UP:
        await this.processTopUp(sessionId, ctx);
        break;
      case PaymentContext.PLAN_CHANGE:
        await this.processPlanChange(sessionId, ctx);
        break;
      case PaymentContext.AUTO_RENEW:
        await this.processAutoRenew(sessionId, ctx);
        break;
      case PaymentContext.ADHOC_INVOICE:
        await this.processAdhocInvoice(sessionId, ctx);
        break;
      case PaymentContext.EQUIPMENT_PURCHASE:
        await this.processEquipmentPurchase(sessionId, ctx);
        break;
      default:
        throw new Error(`Unsupported payment context: ${ctx.context}`);
    }
  }

  private async processTopUp(sessionId: string, ctx: StripePaymentContext): Promise<void> {
    const billingAddr: any = ctx.billingAddress || {};
    const payload: any = {
      CustomerId: ctx.customerId,
      topUpID: ctx.planId || '',
      planId: ctx.planId || '',
      zipCode:
        ctx.customerZipCodeId ||
        billingAddr.zipCode ||
        billingAddr.zip ||
        '',
      chargeCreditCard: 'Y',
      taxAmount: ctx.taxAmount || 0,
      taxIncluded: ctx.taxIncluded ?? true,
      paymentMethodToken: sessionId,
      address: {
        address1: billingAddr.address1 || billingAddr.address || '',
        address2: billingAddr.address2 || '',
        city: billingAddr.city || '',
        state: billingAddr.state || '',
        zip: billingAddr.zipCode || billingAddr.zip || ''
      },
      createTransactionRequest: {
        refId: String(Date.now()),
        transactionRequest: {
          amount: ctx.amount || 0,
          quantity: 1,
          payment: {
            creditCard: {
              cardNumber: '',
              expirationDate: '',
              cardCode: '',
              nameOnCard: ''
            }
          }
        }
      }
    };

    const res: any = await firstValueFrom(
      this.httpService.post('api/Customer/AddCustomerTopUp', payload)
    );
    if (!res?.Status) {
      throw new Error(res?.data || res?.message || 'Top-up payment failed');
    }
  }

  private async processPlanChange(sessionId: string, ctx: StripePaymentContext): Promise<void> {
    const billingAddr: any = ctx.billingAddress || {};

    if (ctx.isConvertToPrepaid) {
      // Fetch live tax amount — same as Braintree flow
      const taxRes: any = await firstValueFrom(
        this.httpService.get('api/Customer/GetPlanTax', {
          CustomerId: ctx.customerId,
          planId: ctx.planId
        })
      );
      const taxAmount: number = taxRes?.data?.taxAmount || 0;

      const taxIncluded: boolean = !!ctx.taxIncluded;

      // Resolve zipCode ID before building payload
      const rawZip = billingAddr.zipCode || billingAddr.zip || '';
      let zipCodeId: any = rawZip;
      if (rawZip) {
        const zipRes: any = await firstValueFrom(
          this.httpService.get('api/system/getZipCodeId', { zipCode: rawZip })
        );
        if (zipRes?.Status && zipRes?.data?.id) {
          zipCodeId = zipRes.data.id;
        }
      }

      const payload: any = {
        planId: Number(ctx.planId) || 0,
        planName: ctx.planName || '',
        intervalLength: 30,
        trailAmount: 0,
        trial_occurrences: 1,
        autoRenew: true,
        paymentMethodToken: sessionId,
        taxAmount,
        taxIncluded,
        chargeCreditCard: taxIncluded ? 'N' : 'Y',
        source: 'demo',
        billingAddress: {
          address1: billingAddr.address1 || billingAddr.address || '',
          address2: billingAddr.address2 || '',
          city: billingAddr.city || '',
          state: billingAddr.state || '',
          zipCode: zipCodeId
        },
        createTransactionRequest: {
          refId: String(Date.now()),
          transactionRequest: {
            amount: ctx.amount || 0,
            planAmount: Number(ctx.planPrice ?? ctx.amount ?? 0),
            quantity: 1,
            payment: {
              creditCard: {
                cardNumber: '',
                expirationDate: '',
                cardCode: '',
                nameOnCard: ''
              }
            }
          }
        }
      };

      const response: any = await firstValueFrom(
        this.httpService.post('api/Customer/ConvertToPrePaid', payload)
      );
      if (!response?.Status) {
        throw new Error(
          response?.data || response?.message || 'Convert to prepaid failed'
        );
      }
      return;
    }

    if (ctx.planChangePaymentType === 'Scheduled') {
      const payload: any = {
        CustomerId: ctx.customerId,
        planId: ctx.planId || '',
        amount: ctx.amount || 0,
        planName: ctx.planName || '',
        paymentMethodToken: sessionId,
        billingAddress: {
          address1: billingAddr.address1 || billingAddr.address || '',
          address2: billingAddr.address2 || '',
          city: billingAddr.city || '',
          state: billingAddr.state || '',
          zipCode:
            ctx.customerZipCodeId ||
            billingAddr.zipCode ||
            billingAddr.zip ||
            ''
        },
        creditCard: {
          cardNumber: '',
          expirationDate: '',
          cardCode: '',
          nameOnCard: ''
        }
      };

      const response: any = await firstValueFrom(
        this.httpService.post('api/Plan/Plan-Profile', payload)
      );
      if (!response?.Status) {
        throw new Error(
          response?.data || response?.message || 'Scheduled plan change failed'
        );
      }
      return;
    }

    const payload: any = {
      CustomerId: ctx.customerId,
      planId: ctx.planId || '',
      planName: ctx.planName || '',
      chargeCreditCard: 'Y',
      taxAmount: ctx.taxAmount || 0,
      taxIncluded: ctx.taxIncluded ?? true,
      isUpdateCard: false,
      paymentMethodToken: sessionId,
      createTransactionRequest: {
        refId: '123456',
        transactionRequest: {
          amount: ctx.amount || 0,
          quantity: 1,
          payment: {
            creditCard: {
              cardNumber: '',
              expirationDate: '',
              cardCode: '',
              nameOnCard: ''
            }
          }
        }
      },
      billingAddress: {
        address1: billingAddr.address1 || billingAddr.address || '',
        address2: billingAddr.address2 || '',
        city: billingAddr.city || '',
        state: billingAddr.state || '',
        zipCode:
          ctx.customerZipCodeId ||
          billingAddr.zipCode ||
          billingAddr.zip ||
          ''
      }
    };

    const response: any = await firstValueFrom(
      this.httpService.post('api/Customer/ChangeCustomerPlan', payload)
    );
    if (!response?.Status) {
      throw new Error(response?.data || response?.message || 'Plan change failed');
    }
  }

  private async processAutoRenew(sessionId: string, ctx: StripePaymentContext): Promise<void> {
    // Resolve subscription ID: context > sessionStorage > API fallback
    let subscriptionId: string =
      ctx.subscriptionId ||
      (sessionStorage.getItem('SubscriptionId') ?? '') ||
      '';

    if (!subscriptionId) {
      const listRes: any = await firstValueFrom(
        this.httpService.get('api/subscription/subscriptionsList', { CustomerId: ctx.customerId })
      );
      const sub: any = listRes?.data?.data?.[0];
      subscriptionId = String(
        sub?.SubscriptionId || sub?.SubscriptionID || sub?.subscriptionId || sub?.id || ''
      );
      if (!subscriptionId) {
        throw new Error('Could not find customer subscription');
      }
    }

    const billingAddr: any = ctx.billingAddress || {};
    const payload: any = {
      CustomerId:       ctx.customerId,
      planId:           ctx.planId    || '',
      planName:         ctx.planName  || '',
      amount:           ctx.amount    || 0,
      chargeCreditCard: 'Y',
      taxAmount:        ctx.taxAmount || 0,
      taxIncluded:      ctx.taxIncluded ?? true,
      paymentMethodToken: sessionId,
      createTransactionRequest: {
        refId: String(Date.now()),
        transactionRequest: {
          amount:   ctx.amount || 0,
          quantity: 1,
          payment: {
            creditCard: {
              cardNumber:     '',
              expirationDate: '',
              cardCode:       '',
              nameOnCard:     ''
            }
          }
        }
      },
      billingAddress: {
        address1: billingAddr.address  || '',
        address2: billingAddr.address2 || '',
        zipCode:  ctx.customerZipCodeId || billingAddr.zip || ''
      }
    };

    const res: any = await firstValueFrom(
      this.httpService.post(`api/subscription/${subscriptionId}/edit`, payload)
    );
    if (!res?.Status) {
      throw new Error(res?.data || res?.message || 'Auto-renew update failed');
    }
  }

  private async processAdhocInvoice(sessionId: string, ctx: StripePaymentContext): Promise<void> {
    const meta: any = ctx.metadata || {};
    const payload: any = {
      customerId:   ctx.customerId,
      invoiceId:    meta['invoiceId'],
      invoiceType:  meta['invoiceType'],
      chargeFrom:   'card',
      paymentMethodToken: sessionId,
      advancePlanId: meta['advancePlanId'] || null
    };

    const res: any = await firstValueFrom(
      this.httpService.post('api/Customer/adhoc/invoices/payNow', payload)
    );
    if (!res?.Status) {
      throw new Error(res?.data || res?.message || 'Invoice payment failed');
    }
  }

  private async processEquipmentPurchase(
    sessionId: string,
    ctx: StripePaymentContext
  ): Promise<void> {
    const cartItems = ctx.cartItems || [];
    if (!cartItems.length) {
      throw new Error('No cart items found for equipment purchase');
    }

    const payload: any = {
      CustomerId: ctx.customerId,
      combos: cartItems.map((item: any) => ({
        comboId: item.comboId,
        products: (item.products || []).map((product: any) => ({
          productId: product.productId
        }))
      })),
      taxIncluded: 1,
      taxAmount: ctx.taxAmount || 0,
      chargeCreditCard: 'Y',
      paymentMethodToken: sessionId,
      createTransactionRequest: {
        refId: String(Date.now()),
        transactionRequest: {
          amount: ctx.amount || 0,
          quantity: 1,
          payment: {
            creditCard: {
              cardNumber: '',
              expirationDate: '',
              cardCode: '',
              nameOnCard: ''
            }
          }
        }
      }
    };

    const response: any = await firstValueFrom(
      this.httpService.post('api/Customer/SaveDeviceCombos', payload)
    );
    if (!response?.Status) {
      throw new Error(
        response?.data || response?.message || 'Equipment purchase failed'
      );
    }
  }

  private getSuccessMessage(context: PaymentContext): string {
    switch (context) {
      case PaymentContext.TOP_UP:
        return 'Your data top-up has been applied successfully.';
      case PaymentContext.PLAN_CHANGE:
        return 'Your plan change has been processed successfully.';
      case PaymentContext.AUTO_RENEW:    return 'Auto-renew has been enabled successfully.';
      case PaymentContext.EQUIPMENT_PURCHASE:
        return 'Your equipment purchase has been processed successfully.';
      case PaymentContext.ADHOC_INVOICE: return 'Your invoice payment has been processed successfully.';
      default:                           return 'Your payment has been processed successfully.';
    }
  }

  private startCountdown(): void {
    this.countdownTimer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(this.countdownTimer);
        this.redirectBack();
      }
    }, 1000);
  }

  private redirectBack(): void {
    if (this.returnUrl) {
      // Strip hash prefix for Angular's hash router
      const path = this.returnUrl.replace(/^.*#/, '') || '/dashboard';
      this.router.navigateByUrl(path);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  goBack(): void {
    this.redirectBack();
  }

  private cleanup(): void {
    localStorage.removeItem('stripePaymentContext');
    localStorage.removeItem('stripePaymentDetails');
    sessionStorage.removeItem('SubscriptionId');
  }
}
